import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Btn, Eyebrow } from "../components/Primitives";
import AppShell from "../components/AppShell";
import { accountsApi, formatAuthError } from "../lib/accountsApi";

export default function DeleteAccountRequest() {
    const [email, setEmail] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const submit = async (e) => {
        e?.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await accountsApi.deletionRequest(email.trim(), reason.trim());
            setDone(true);
        } catch (err) {
            setError(formatAuthError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AppShell>
            <div className="flex-1 flex flex-col px-7 py-10" data-testid="delete-account-page">
                <Eyebrow>Ever After</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-tight">
                    Delete your <span className="italic">Ever After</span> account.
                </h1>

                {done ? (
                    <div className="mt-8" data-testid="delete-request-success">
                        <p className="font-display italic text-2xl text-burgundy">Request received.</p>
                        <p className="mt-3 text-sm text-ink leading-relaxed">
                            We'll verify your request and delete your personal data within 30 days. You'll hear from us at <span className="text-burgundy font-medium">{email.trim()}</span> if we need anything else.
                        </p>
                        <Link to="/" className="mt-6 inline-block text-[11px] tracking-[0.28em] uppercase text-burgundy underline underline-offset-4">
                            ← Back to Ever After
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="mt-5 text-sm text-ink leading-relaxed space-y-3">
                            <p>
                                Use this form to request deletion of your Ever After account and the personal data associated with it — your profile, assessment answers and archetype results, photos, AI conversations, and sign-in credentials.
                            </p>
                            <p>
                                Personal data is permanently deleted within <span className="font-semibold text-burgundy">30 days</span> of a verified request. Transaction records are retained for 7 years as required by law, and safety-related records are retained to protect our community.
                            </p>
                            <p className="text-ink-muted">
                                If you have an active subscription, please cancel it first — deleting your account does not cancel billing with your app store or payment provider.
                            </p>
                        </div>

                        <form onSubmit={submit} className="mt-7 space-y-4">
                            <label className="block">
                                <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">Account email</div>
                                <input
                                    data-testid="delete-request-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"
                                />
                            </label>
                            <label className="block">
                                <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">Reason for leaving</div>
                                <textarea
                                    data-testid="delete-request-reason"
                                    required
                                    rows={4}
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none resize-none"
                                />
                            </label>
                            {error && (
                                <div data-testid="delete-request-error" className="text-sm text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2">{error}</div>
                            )}
                            <Btn
                                data-testid="delete-request-submit"
                                type="submit"
                                disabled={!validEmail || !reason.trim() || submitting}
                                className={!validEmail || !reason.trim() || submitting ? "opacity-50 cursor-not-allowed" : ""}
                            >
                                {submitting ? "Submitting…" : "Request account deletion"}
                            </Btn>
                        </form>

                        <p className="mt-5 text-xs text-ink-muted">
                            Requests are handled by privacy@youreverafter.com. Signed in already? You can delete instantly from{" "}
                            <Link to="/settings" data-testid="delete-request-settings-link" className="text-burgundy underline underline-offset-4">Settings → Privacy</Link>.
                        </p>
                    </>
                )}
            </div>
        </AppShell>
    );
}
